"use client"

import * as React from "react"
import { createContext, useContext, useEffect } from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = Readonly<{
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}>

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme?: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  useEffect(() => {
    const root = globalThis.document.documentElement

    root.classList.remove("light", "dark")
    root.classList.add("dark")
  }, [])

  const value = React.useMemo(
    () => ({
      theme: "dark" as Theme,
      setTheme: () => {
        const forcedTheme: Theme = "dark"
        globalThis.localStorage.setItem(storageKey, forcedTheme)
      },
    }),
    [storageKey],
  )

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}